import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { paymentApi } from '@formosa/shared/api/payment';
import { ApiError } from '@formosa/shared/api/request';
import { needsKycForWithdraw } from '@formosa/shared/utils/kycConfig';
import KycPromptModal from '../../components/kyc/KycPromptModal';
import WithdrawAddressPickerSheet from '../../components/withdraw/WithdrawAddressPickerSheet';
import PasswordField from '../../components/security/PasswordField';
import WithdrawAddressPromptModal from '../../components/withdraw/WithdrawAddressPromptModal';
import { normalizeRealNameStatus } from '../../constants/kycConstants';
import { resolveImageUrl } from '../../constants/depositConstants';
import { formatFundAmount, tf } from '../../constants/fundConstants';
import {
  calcWithdrawFee,
  resolveWithdrawFeeRate,
  isBankWithdrawOption,
  listWithdrawableOptions,
  resolveWithdrawAddressFieldLabel,
  resolveWithdrawCurrencyName,
  resolveWithdrawCurrencyTitle,
  resolveWithdrawMaxAmount,
  resolveWithdrawPickerLabel,
  resolveWithdrawSelectorSubtitle,
} from '../../constants/withdrawConstants';
import { useWallet } from '../../context/WalletContext';
import { useTranslation } from '../../i18n/LocaleContext';

export default function WithdrawPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { balance, refreshWallet } = useWallet();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [channels, setChannels] = useState([]);
  const [kycConfig, setKycConfig] = useState(null);
  const [paymentSetting, setPaymentSetting] = useState(null);
  const [realNameStatus, setRealNameStatus] = useState('');
  const [hasJyPassword, setHasJyPassword] = useState(true);
  const [kycPromptOpen, setKycPromptOpen] = useState(false);
  const [addressPromptOpen, setAddressPromptOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copyHint, setCopyHint] = useState('');

  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');

  const withdrawableOptions = useMemo(() => {
    const rows = listWithdrawableOptions(addresses, channels);
    return rows.map((item) => ({
      ...item,
      pickerLabel: resolveWithdrawPickerLabel(item),
    }));
  }, [addresses, channels]);

  const selectedOption = useMemo(
    () => withdrawableOptions.find((item) => item.optionId === selectedOptionId) || null,
    [withdrawableOptions, selectedOptionId]
  );

  const availableBalance = Number(balance) || 0;
  const maxWithdrawAmount = useMemo(
    () => (selectedOption ? resolveWithdrawMaxAmount(selectedOption, availableBalance) : availableBalance),
    [selectedOption, availableBalance]
  );

  const withdrawFeeRate = useMemo(
    () => resolveWithdrawFeeRate(selectedOption, paymentSetting?.withdrawFee),
    [selectedOption, paymentSetting]
  );

  const feeAmount = useMemo(
    () => calcWithdrawFee(amount, withdrawFeeRate),
    [amount, withdrawFeeRate]
  );

  const receiveAmount = useMemo(() => {
    const value = Number(amount) || 0;
    return Math.max(value - feeAmount, 0);
  }, [amount, feeAmount]);

  const canSubmit = useMemo(
    () => Boolean(selectedOption)
      && Number(amount) > 0
      && Number(amount) <= maxWithdrawAmount
      && !submitting
      && !kycPromptOpen
      && !addressPromptOpen,
    [selectedOption, amount, maxWithdrawAmount, submitting, kycPromptOpen, addressPromptOpen]
  );

  const pageReady = !loading && !kycPromptOpen && !addressPromptOpen && withdrawableOptions.length > 0;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      memberApi.getKycConfig(),
      memberApi.getPaymentSettingConfig(),
      memberApi.getInfo(),
      paymentApi.listWithdrawAddresses(),
      paymentApi.listWithdrawChannelOptions(),
    ])
      .then(([config, paymentConfig, info, addressList, channelList]) => {
        if (cancelled) {
          return;
        }
        const boundAddresses = Array.isArray(addressList) ? addressList : [];
        const activeChannels = Array.isArray(channelList) ? channelList : [];
        const status = normalizeRealNameStatus(info?.realNameStatus);
        const options = listWithdrawableOptions(boundAddresses, activeChannels);

        setKycConfig(config);
        setPaymentSetting(paymentConfig);
        setRealNameStatus(status);
        setHasJyPassword(Boolean(info?.hasJyPassword));
        setAddresses(boundAddresses);
        setChannels(activeChannels);

        if (needsKycForWithdraw(config, status)) {
          setKycPromptOpen(true);
          return;
        }
        if (boundAddresses.length === 0) {
          setAddressPromptOpen(true);
          return;
        }
        if (options.length === 0) {
          setError(t('withdraw.noWithdrawableChannel'));
          return;
        }
        setSelectedOptionId(options[0].optionId);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('withdraw.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  function handleKycLater() {
    setKycPromptOpen(false);
    navigate(-1);
  }

  function handleKycSetup() {
    setKycPromptOpen(false);
    navigate('/kyc', { state: { from: '/withdraw' } });
  }

  function handleAddressLater() {
    setAddressPromptOpen(false);
    navigate(-1);
  }

  function handleAddressSetup() {
    setAddressPromptOpen(false);
    navigate('/withdraw/methods', { state: { from: '/withdraw' } });
  }

  function handlePickerConfirm(optionId) {
    setSelectedOptionId(optionId);
    setPickerOpen(false);
    setAmount('');
    setError('');
  }

  async function handleCopyAddress() {
    const text = selectedOption?.walletAddress;
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint(t('withdraw.copied'));
      window.setTimeout(() => setCopyHint(''), 2000);
    } catch {
      setCopyHint(t('withdraw.copyFailed'));
    }
  }

  function handleUseMax() {
    setAmount(String(maxWithdrawAmount));
  }

  function handleSubmitClick() {
    setError('');
    if (needsKycForWithdraw(kycConfig, realNameStatus)) {
      setKycPromptOpen(true);
      return;
    }
    if (!selectedOption) {
      setError(t('withdraw.selectAddress'));
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError(t('withdraw.invalidAmount'));
      return;
    }
    if (Number(amount) > maxWithdrawAmount) {
      setError(tf(t, 'withdraw.exceedMaxAmount', { amount: formatFundAmount(maxWithdrawAmount) }));
      return;
    }
    if (!hasJyPassword) {
      navigate('/security/withdraw-password', { state: { from: '/withdraw' } });
      return;
    }
    setPasswordModalOpen(true);
  }

  async function handleConfirmWithdraw(event) {
    event.preventDefault();
    if (!password.trim()) {
      setError(t('withdraw.passwordRequired'));
      return;
    }
    if (!selectedOption) {
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        amount: Number(amount),
        password,
        channelType: selectedOption.channelType,
        withdrawChannelId: selectedOption.channel?.channelId,
        currencyName: resolveWithdrawCurrencyName(selectedOption),
        address: selectedOption.walletAddress,
      };
      if (isBankWithdrawOption(selectedOption)) {
        payload.realName = selectedOption.walletName;
        payload.bankCardNumber = selectedOption.walletAddress;
      }
      await paymentApi.withdrawOrder(payload);
      setPasswordModalOpen(false);
      setAmount('');
      setPassword('');
      await refreshWallet();
      navigate('/wallet', { state: { withdrawSubmitted: true } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('withdraw.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const currencyTitle = selectedOption
    ? resolveWithdrawCurrencyTitle(selectedOption)
    : DEFAULT_WITHDRAW_CURRENCY_LABEL;
  const amountLabel = selectedOption
    ? tf(t, 'withdraw.amountLabel', { currency: currencyTitle })
    : t('withdraw.amountTitle');

  return (
    <div className="deposit-page withdraw-page withdraw-page--v2">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('withdraw.title')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      <KycPromptModal
        open={kycPromptOpen}
        onLater={handleKycLater}
        onSetup={handleKycSetup}
      />
      <WithdrawAddressPromptModal
        open={addressPromptOpen}
        onLater={handleAddressLater}
        onSetup={handleAddressSetup}
      />

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      {pageReady && selectedOption && (
        <>
          <section className="deposit-field">
            <div className="deposit-field__label">{t('withdraw.currency')}</div>
            <button
              type="button"
              className="deposit-currency-select"
              onClick={() => setPickerOpen(true)}
            >
              <span className="deposit-currency-select__left">
                {selectedOption.channel?.image ? (
                  <img src={resolveImageUrl(selectedOption.channel.image)} alt="" />
                ) : (
                  <span className={`deposit-currency-select__fallback ${isBankWithdrawOption(selectedOption) ? 'is-bank' : ''}`}>
                    {isBankWithdrawOption(selectedOption) ? '¥' : '₮'}
                  </span>
                )}
                <strong>{currencyTitle}</strong>
              </span>
              <span className="deposit-currency-select__right">
                {resolveWithdrawSelectorSubtitle(selectedOption)}
                <span aria-hidden>▾</span>
              </span>
            </button>
          </section>

          <section className="deposit-field">
            <div className="withdraw-field__row">
              <div className="deposit-field__label">
                {resolveWithdrawAddressFieldLabel(selectedOption, t)}
              </div>
              {copyHint && <span className="withdraw-field__copy-hint">{copyHint}</span>}
            </div>
            <div className="withdraw-address-display">
              <span className="withdraw-address-display__text">{selectedOption.walletAddress}</span>
              <button
                type="button"
                className="withdraw-address-display__copy"
                aria-label={t('withdraw.copyAddress')}
                onClick={handleCopyAddress}
              >
                ⧉
              </button>
            </div>
          </section>

          <section className="deposit-amount-card">
            <div className="deposit-amount-card__title">
              {tf(t, 'withdraw.availableAmount', { amount: formatFundAmount(availableBalance) })}
            </div>
            <div className="deposit-amount-card__body">
              <div className="withdraw-amount-card__label-row">
                <span>{amountLabel}</span>
                <span>{tf(t, 'withdraw.maxAmount', { amount: formatFundAmount(maxWithdrawAmount) })}</span>
              </div>
              <div className="deposit-amount-card__input-row">
                <span className={`deposit-amount-card__symbol ${isBankWithdrawOption(selectedOption) ? 'is-bank' : ''}`}>
                  {selectedOption.channel?.image ? (
                    <img src={resolveImageUrl(selectedOption.channel.image)} alt="" />
                  ) : (
                    isBankWithdrawOption(selectedOption) ? '¥' : '₮'
                  )}
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0.0000"
                />
                <button type="button" className="deposit-amount-card__all" onClick={handleUseMax}>
                  {t('deposit.all')}
                </button>
              </div>
              <div className="deposit-amount-card__approx">
                ≈ {formatFundAmount(amount)} ({resolveWithdrawCurrencyName(selectedOption)})
              </div>
              <div className="deposit-amount-card__estimate">
                <span>{t('withdraw.estimatedArrival')}</span>
                <strong>
                  {formatFundAmount(receiveAmount)}
                  <span className="deposit-amount-card__approx deposit-amount-card__approx--inline">
                    ≈ {formatFundAmount(receiveAmount)} ({resolveWithdrawCurrencyName(selectedOption)})
                  </span>
                </strong>
              </div>
            </div>
          </section>

          <p className="deposit-footnotes">{t('withdraw.exchangeRateNote')}</p>

          <section className="withdraw-fee-card">
            <div className="withdraw-fee-card__row">
              <span>{t('withdraw.handlingFee')}</span>
              <span>
                {formatFundAmount(feeAmount)}
                <span className="withdraw-fee-card__sub">
                  ≈ {formatFundAmount(feeAmount)} ({resolveWithdrawCurrencyName(selectedOption)})
                </span>
              </span>
            </div>
            <p className="withdraw-fee-card__note">{t('withdraw.feeNote')}</p>
          </section>

          <button
            type="button"
            className={`withdraw-page__submit ${canSubmit ? 'is-ready' : ''}`}
            disabled={!canSubmit}
            onClick={handleSubmitClick}
          >
            {submitting ? t('common.loading') : t('withdraw.submit')}
          </button>
        </>
      )}

      <WithdrawAddressPickerSheet
        open={pickerOpen}
        options={withdrawableOptions}
        selectedId={selectedOptionId}
        onClose={() => setPickerOpen(false)}
        onConfirm={handlePickerConfirm}
      />

      {passwordModalOpen && (
        <div className="trade-modal trade-modal--center">
          <button
            type="button"
            className="trade-modal__backdrop"
            aria-label={t('common.close')}
            onClick={() => setPasswordModalOpen(false)}
          />
          <div className="trade-modal__panel trade-modal__panel--compact withdraw-password-modal">
            <header className="trade-modal__header">
              <h2>{t('withdraw.passwordTitle')}</h2>
              <button
                type="button"
                className="trade-modal__close"
                aria-label={t('common.close')}
                onClick={() => setPasswordModalOpen(false)}
              >
                ×
              </button>
            </header>
            <form className="withdraw-password-modal__form" onSubmit={handleConfirmWithdraw}>
              <PasswordField
                id="withdraw-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('withdraw.passwordLabel')}
                autoComplete="current-password"
              />
              <div className="withdraw-password-modal__actions">
                <button
                  type="button"
                  className="withdraw-password-modal__cancel"
                  onClick={() => setPasswordModalOpen(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="withdraw-password-modal__confirm" disabled={submitting}>
                  {submitting ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_WITHDRAW_CURRENCY_LABEL = 'USDT';
